'use client'

import { createContext, type ReactNode, type RefObject, use } from 'react'

import type { MenuItem, MenuSeparator, NavGroup } from '@graftcode/design-system'

import { MOBILE_MENU_TEST_IDS } from '@common/common-test-ids.const'
import { AppVariant } from '@common/enums'
import type { useAuthUser } from '@common/hooks/useAuthUser'

import type { Project } from '@modules/projects/projects.type'
import type { Workspace } from '@modules/workspaces'

export type MenuView = 'main' | 'projects' | 'workspaces'

type Auth0User = ReturnType<typeof useAuthUser>['user']

export interface MobileMenuContextValue {
  appVariant: AppVariant
  auth0User: Auth0User
  contentRef: RefObject<HTMLDivElement | null>
  currentProject: Project | null
  currentView: MenuView
  currentWorkspace: Workspace | null
  displayProjectName: string
  displayUserEmail: string
  displayUserName: string
  displayWorkspaceInitials: string
  displayWorkspaceName: string
  helpItems?: (MenuItem | MenuSeparator)[]
  isAccountDrawerOpen: boolean
  isAppPickerOpen: boolean
  isHelpDrawerOpen: boolean
  isModulesMenu: boolean
  isOpen: boolean
  isProjectPickerOpen: boolean
  isScrolled: boolean
  menuGroups: NavGroup[]
  mobileMenuProjects: Project[]
  navigateToMain: () => void
  navigateToProjects: () => void
  navigateToWorkspaces: () => void
  navigationTopSlot?: ReactNode
  notificationBadge?: number
  onClose: () => void
  onCreateProject?: () => void
  onHelpClick?: (itemId: string) => void
  onInviteProjectClick?: () => void
  onInviteWorkspaceClick?: () => void
  onNavigationClick?: (url: string) => void
  onNotificationClick?: () => void
  onOpenProjectMembers?: () => void
  onOpenProjectSettings?: () => void
  onOpenWorkspaceSettings?: () => void
  onProfileClick?: (itemId: string) => void
  onProjectClick?: (projectId: string) => void
  onWorkspaceClick?: (workspaceId: string) => void
  renderHeader?: () => ReactNode
  setCurrentView: (view: MenuView) => void
  setIsAccountDrawerOpen: (open: boolean) => void
  setIsAppPickerOpen: (open: boolean) => void
  setIsHelpDrawerOpen: (open: boolean) => void
  setIsProjectPickerOpen: (open: boolean) => void
  showHelpSelector: boolean
  showProjectSelector: boolean
  showWorkspaceSelector: boolean
  showUserAvatar?: boolean
  userAvatarUrl?: string
  viewClassName: string
  workspaces: Workspace[]
}

const MobileMenuContext = createContext<MobileMenuContextValue | null>(null)

export function useMobileMenuContext() {
  const context = use(MobileMenuContext)
  if (!context) {
    throw new Error('useMobileMenuContext must be used within MobileMenuProvider')
  }
  return context
}

interface MobileMenuProviderProps {
  children: ReactNode
  value: MobileMenuContextValue
}

export function MobileMenuProvider({ children, value }: MobileMenuProviderProps) {
  return (
    <div data-testid={MOBILE_MENU_TEST_IDS.provider} style={{ display: 'contents' }}>
      <MobileMenuContext value={value}>{children}</MobileMenuContext>
    </div>
  )
}
